import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client.js';
import { formatBrl } from '../../utils/currency.js';
import { openSaasFinanceReportPrintWindow } from '../../utils/saasFinanceReportPrint.js';
import {
  AdminSubscriptionPayButton,
  AdminSubscriptionPortalLink,
} from './AdminSubscriptionPortalActions.jsx';

function localYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function rowBillingLabel(row, refTodayYmd) {
  const td = String(refTodayYmd || '').slice(0, 10);
  const due = String(row?.due_date || '').slice(0, 10);
  if (String(row?.status) === 'paid') return 'paga';
  if (String(row?.status) === 'cancelled') return 'cancelada';
  if (String(row?.status) !== 'pending') return String(row.status || '');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(due)) return 'em aberto';
  if (due < td) return 'em atraso';
  if (due === td) return 'vence hoje';
  return 'em aberto';
}

function LedgerSaaSKpi({ label, value, detail, tint = 'rose' }) {
  const cls =
    tint === 'violet'
      ? 'border-violet-500/35 from-violet-400/25'
      : tint === 'sky'
        ? 'border-sky-500/35 from-sky-400/20'
        : tint === 'emerald'
          ? 'border-emerald-500/40 from-emerald-400/30'
          : 'border-rose-500/38 from-rose-400/25';
  const labelDark =
    tint === 'violet'
      ? 'dark:text-violet-200'
      : tint === 'sky'
        ? 'dark:text-sky-200'
        : tint === 'emerald'
          ? 'dark:text-emerald-200'
          : 'dark:text-rose-200';
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br ${cls} to-transparent p-5 backdrop-blur-sm dark:to-transparent dark:bg-white/[0.05]`}
    >
      <p className={`text-[10px] font-bold uppercase tracking-[0.2em] text-ink/55 ${labelDark}`}>{label}</p>
      <p className="mt-2 font-display text-2xl font-semibold tabular-nums text-ink dark:text-frost">{value}</p>
      {detail ? (
        <p className="mt-2 text-[11px] leading-relaxed text-ink/60 dark:text-frost-muted">{detail}</p>
      ) : null}
    </div>
  );
}

export function AdminSaasFinancePanel() {
  const [msg, setMsg] = useState('');
  const [busyDash, setBusyDash] = useState(true);
  const [busyList, setBusyList] = useState(false);
  const [dash, setDash] = useState(null);
  const [users, setUsers] = useState([]);
  const [tenantRows, setTenantRows] = useState([]);
  const [charges, setCharges] = useState([]);
  const [chargesTotal, setChargesTotal] = useState(0);
  const [chargeBucket, setChargeBucket] = useState('');
  const [chargeStatus, setChargeStatus] = useState('');
  const [tenantFilterCharges, setTenantFilterCharges] = useState('');
  const [ownerFilterCharges, setOwnerFilterCharges] = useState('');
  const [shFilterCharges, setShFilterCharges] = useState('');
  const [softwareHouses, setSoftwareHouses] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [expandedUsers, setExpandedUsers] = useState(() => Object.create(null));
  const chargeLimit = 50;
  const [chargeOffset, setChargeOffset] = useState(0);

  /** @type {[null | Record<string, unknown>, function]} */
  const [editCharge, setEditCharge] = useState(null);
  const [editForm, setEditForm] = useState({
    amount_brl: '',
    due_date: '',
    period_label: '',
    period_start: '',
    period_end: '',
  });
  const [editSaving, setEditSaving] = useState(false);

  const loadDash = useCallback(async () => {
    setBusyDash(true);
    setMsg('');
    try {
      const d = await api('/admin/subscription/dashboard');
      setDash(d);
    } catch (e) {
      setDash(null);
      setMsg(e.message || 'Erro ao carregar dashboard SaaS.');
    } finally {
      setBusyDash(false);
    }
  }, []);

  const loadChargesPage = useCallback(async () => {
    setBusyList(true);
    try {
      const p = new URLSearchParams();
      p.set('limit', String(chargeLimit));
      p.set('offset', String(chargeOffset));
      const tid = Number(tenantFilterCharges);
      if (Number.isFinite(tid) && tid > 0) p.set('tenant_id', String(tid));
      const oid = Number(ownerFilterCharges);
      if (Number.isFinite(oid) && oid > 0) p.set('owner_user_id', String(oid));
      if (shFilterCharges === 'none') {
        p.set('software_house_id', 'none');
      } else {
        const shid = Number(shFilterCharges);
        if (Number.isFinite(shid) && shid > 0) p.set('software_house_id', String(shid));
      }
      if (chargeStatus) p.set('status', chargeStatus);
      if (chargeBucket) p.set('bucket', chargeBucket);
      const qs = p.toString() ? `?${p}` : '';
      const d = await api(`/admin/subscription/charges${qs}`);
      setCharges(d.charges || []);
      setChargesTotal(Number(d.total || 0));
    } catch (e) {
      setCharges([]);
      setMsg(e.message || 'Erro ao listar cobranças SaaS.');
    } finally {
      setBusyList(false);
    }
  }, [chargeOffset, tenantFilterCharges, ownerFilterCharges, shFilterCharges, chargeStatus, chargeBucket]);

  useEffect(() => {
    loadDash().catch(() => {});
    (async () => {
      try {
        const u = await api('/admin/users');
        setUsers(u.users || []);
      } catch {
        setUsers([]);
      }
      try {
        const t = await api('/admin/tenants');
        setTenantRows(t.tenants || []);
      } catch {
        setTenantRows([]);
      }
      try {
        const sh = await api('/admin/software-houses');
        setSoftwareHouses(sh.software_houses || []);
      } catch {
        setSoftwareHouses([]);
      }
    })();
  }, [loadDash]);

  useEffect(() => {
    loadChargesPage().catch(() => {});
  }, [loadChargesPage]);

  const clients = useMemo(
    () => (users || []).filter((u) => u.role !== 'super_admin'),
    [users],
  );

  const tenantsByOwner = useMemo(() => {
    const m = Object.create(null);
    for (const t of tenantRows || []) {
      const oid = Number(t.user_id);
      if (!Number.isFinite(oid)) continue;
      if (!m[oid]) m[oid] = [];
      m[oid].push(t);
    }
    return m;
  }, [tenantRows]);

  function openEdit(c) {
    setEditCharge(c);
    setEditForm({
      amount_brl: c.amount_brl != null ? String(Number(c.amount_brl)).replace('.', ',') : '',
      due_date: String(c.due_date || '').slice(0, 10),
      period_label: c.period_label != null ? String(c.period_label) : '',
      period_start: String(c.period_start || '').slice(0, 10),
      period_end: String(c.period_end || '').slice(0, 10),
    });
  }

  function parseMoneyFlexible(s) {
    const raw = String(s ?? '').trim().replace(/\s/g, '');
    if (raw === '') return NaN;
    if (raw.includes(',')) return Number(raw.replace(/\./g, '').replace(',', '.'));
    return Number(raw);
  }

  async function saveChargeEdit(ev) {
    ev.preventDefault();
    if (!editCharge) return;
    const cid = Number(editCharge.id);
    setEditSaving(true);
    setMsg('');
    try {
      const amt = parseMoneyFlexible(editForm.amount_brl);
      if (!Number.isFinite(amt) || amt < 0) {
        setMsg('Valor (R$) inválido.');
        return;
      }
      await api(`/admin/subscription/charges/${cid}`, {
        method: 'PATCH',
        body: JSON.stringify({
          amount_brl: amt,
          due_date: editForm.due_date.trim(),
          period_label: editForm.period_label.trim() === '' ? null : editForm.period_label.trim(),
          period_start: editForm.period_start.trim() === '' ? null : editForm.period_start.trim(),
          period_end: editForm.period_end.trim() === '' ? null : editForm.period_end.trim(),
        }),
      });
      setEditCharge(null);
      await loadDash();
      await loadChargesPage();
    } catch (e) {
      setMsg(e.message || 'Falha ao gravar cobrança.');
    } finally {
      setEditSaving(false);
    }
  }

  async function cancelChargeConfirmed() {
    if (!editCharge) return;
    if (!window.confirm('Cancelar definitivamente esta cobrança pendente (não paga)?')) return;
    setEditSaving(true);
    setMsg('');
    try {
      await api(`/admin/subscription/charges/${editCharge.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'cancelled' }),
      });
      setEditCharge(null);
      await loadDash();
      await loadChargesPage();
    } catch (e) {
      setMsg(e.message || 'Falha ao cancelar.');
    } finally {
      setEditSaving(false);
    }
  }

  function toggleUser(uid) {
    setExpandedUsers((prev) => ({ ...prev, [uid]: !prev[uid] }));
  }

  const chargeFilterLabels = useMemo(() => {
    const periodoMap = {
      overdue: 'Só em atraso',
      due_week: 'Vence em 7 dias',
      future_pending: 'Pendentes mais distantes',
    };
    const statusMap = {
      pending: 'Em aberto',
      paid: 'Pago',
      cancelled: 'Cancelado',
    };
    /** @type{Record<string, string>} */
    const out = {};
    if (chargeBucket && periodoMap[chargeBucket]) out.periodo = periodoMap[chargeBucket];
    if (chargeStatus && statusMap[chargeStatus]) out.status = statusMap[chargeStatus];
    if (tenantFilterCharges) {
      const t = tenantRows.find((x) => String(x.id) === String(tenantFilterCharges));
      out.empresa = t ? `#${t.id} ${t.nome}` : `#${tenantFilterCharges}`;
    }
    if (ownerFilterCharges) {
      const u = clients.find((x) => String(x.id) === String(ownerFilterCharges));
      out.dono = u ? `${u.name} (#${u.id})` : `#${ownerFilterCharges}`;
    }
    if (shFilterCharges === 'none') {
      out.sh = 'Sem Software House';
    } else if (shFilterCharges) {
      const sh = softwareHouses.find((x) => String(x.id) === String(shFilterCharges));
      out.sh = sh ? String(sh.nome) : `#${shFilterCharges}`;
    }
    return out;
  }, [
    chargeBucket,
    chargeStatus,
    tenantFilterCharges,
    ownerFilterCharges,
    shFilterCharges,
    tenantRows,
    clients,
    softwareHouses,
  ]);

  async function generateFinancePdf() {
    setPdfBusy(true);
    setMsg('');
    try {
      const p = new URLSearchParams();
      p.set('limit', '1000');
      p.set('offset', '0');
      const tid = Number(tenantFilterCharges);
      if (Number.isFinite(tid) && tid > 0) p.set('tenant_id', String(tid));
      const oid = Number(ownerFilterCharges);
      if (Number.isFinite(oid) && oid > 0) p.set('owner_user_id', String(oid));
      if (shFilterCharges === 'none') {
        p.set('software_house_id', 'none');
      } else {
        const shid = Number(shFilterCharges);
        if (Number.isFinite(shid) && shid > 0) p.set('software_house_id', String(shid));
      }
      if (chargeStatus) p.set('status', chargeStatus);
      if (chargeBucket) p.set('bucket', chargeBucket);
      const d = await api(`/admin/subscription/charges?${p}`);
      const rows = d.charges || [];
      const total = Number(d.total || 0);
      if (total > rows.length) {
        setMsg(
          `Relatório gerado com as primeiras ${rows.length} de ${total} cobranças. Refine os filtros para exportar tudo.`,
        );
      }
      openSaasFinanceReportPrintWindow({
        charges: rows,
        filters: chargeFilterLabels,
        todayYmd: String(dash?.today || localYmd()).slice(0, 10),
      });
    } catch (e) {
      setMsg(e.message || 'Falha ao gerar relatório PDF.');
    } finally {
      setPdfBusy(false);
    }
  }

  return (
    <div className="space-y-10">
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-violet-500/25 bg-violet-500/[0.07] px-6 py-5 dark:bg-violet-950/25">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-violet-900/85 dark:text-violet-100/85">
            Assinaturas SaaS por empresa
          </p>
          <h4 className="mt-2 font-display text-xl font-semibold text-ink dark:text-frost">
            Vencimentos, atrasos e lojas por utilizador
          </h4>
          <p className="mt-2 max-w-3xl text-sm text-ink/65 dark:text-frost-muted">
            Aqui vê todas as mensalidades da plataforma. Pode ajustar valor e datas de cobranças em aberto, cancelar quando
            fizer falta ao suporte, e abrir o mesmo portal de pagamento que o cliente usa em{' '}
            <span className="font-mono text-ink/80 dark:text-mist/90">/app/mensalidade</span> — sem depender do e-mail do
            dono da loja.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <AdminSubscriptionPortalLink
            className="rounded-2xl border border-sky-600/35 bg-sky-500/10 px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-sky-950 shadow-sm dark:border-sky-400/35 dark:bg-sky-500/15 dark:text-sky-50"
          >
            Abrir portal mensalidade
          </AdminSubscriptionPortalLink>
          <button
            type="button"
            onClick={() => {
              loadDash().catch(() => {});
              loadChargesPage().catch(() => {});
            }}
            disabled={busyDash}
            className="rounded-2xl border border-violet-600/35 bg-white/90 px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-violet-950 shadow-sm dark:border-violet-400/35 dark:bg-violet-500/15 dark:text-violet-50"
          >
            Atualizar dados
          </button>
        </div>
      </div>

      {msg ? (
        <div className="rounded-2xl border border-amber-500/45 bg-amber-500/10 px-4 py-3 text-sm font-semibold text-amber-950 dark:text-amber-100">
          {msg}
        </div>
      ) : null}

      {busyDash && !dash ? (
        <p className="text-sm text-ink/55 dark:text-frost-muted">A carregar indicadores…</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <LedgerSaaSKpi
            tint="rose"
            label="Atrasado · em aberto"
            value={`${dash?.overdue?.count ?? 0} · ${formatBrl(dash?.overdue?.amount_brl)}`}
            detail="Cobranças em aberto cujo vencimento já passou — podem bloquear a loja."
          />
          <LedgerSaaSKpi
            tint="sky"
            label="Próximos 7 dias"
            value={`${dash?.due_next_7_days?.count ?? 0} · ${formatBrl(dash?.due_next_7_days?.amount_brl)}`}
            detail="Pendentes previstas até uma semana a seguir ao dia de referência (`today` backend)."
          />
          <LedgerSaaSKpi
            tint="emerald"
            label="Liquidações (últimos 30 d)"
            value={`${dash?.paid_last_30_days?.count ?? 0} · ${formatBrl(dash?.paid_last_30_days?.amount_brl)}`}
            detail="Mensalidades marcadas pagas através do Mercado Pago nos últimos 30 dias (server date)."
          />
          <LedgerSaaSKpi
            tint="violet"
            label="Lojas bloqueadas (SaaS)"
            value={`${dash?.blocked_stores_count ?? 0}`}
            detail={`Total em aberto: ${dash?.pending_totals?.count ?? 0} · ${formatBrl(dash?.pending_totals?.amount_brl)}`}
          />
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-2">
        <MiniChargeTable title="Lista prioridade — em atraso" rows={dash?.overdue?.items || []} onEdit={openEdit} showPay />
        <MiniChargeTable title="Lista — vencimento ≤ 7 dias" rows={dash?.due_next_7_days?.items || []} onEdit={openEdit} showPay />
      </div>

      <div className="space-y-4 rounded-3xl border border-ink/10 bg-white/70 p-6 dark:border-white/10 dark:bg-white/[0.06]">
        <h4 className="font-display text-lg font-semibold text-ink dark:text-mist">Clientes · todas as lojas por conta</h4>
        <p className="text-xs text-ink/60 dark:text-frost-muted">
          Utilizadores da plataforma (excepto admin global); expanda cada um para rever lojas associadas ao contrato SaaS por
          empresa — planos e overrides ficam também em Painel empresas.
        </p>
        <ul className="space-y-3">
          {clients.map((u) => {
            const stores = tenantsByOwner[u.id] || [];
            const open = !!expandedUsers[u.id];
            return (
              <li key={u.id} className="rounded-2xl border border-ink/10 bg-black/[0.02] dark:border-white/10 dark:bg-white/[0.06]">
                <button
                  type="button"
                  className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left"
                  onClick={() => toggleUser(u.id)}
                >
                  <div>
                    <span className="font-semibold text-ink dark:text-mist">{u.name}</span>
                    <span className="ml-2 text-xs font-mono text-ink/55 dark:text-frost">{u.email}</span>
                    <span className="mt-1 block text-[11px] font-medium text-ink/55 dark:text-frost-muted">
                      #{u.id} · conta {String(u.billing_plan || '')}
                      {u.blocked ? ' · utilizador bloqueado' : ''}
                      {stores.length ? ` · ${stores.length} loja${stores.length > 1 ? 's' : ''}` : ' · sem lojas'}
                    </span>
                  </div>
                  <span className="shrink-0 rounded-lg border border-violet-500/30 bg-violet-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-violet-800 dark:border-violet-400/35 dark:bg-violet-500/15 dark:text-violet-100">
                    {open ? 'Recolher' : 'Expandir'}
                  </span>
                </button>
                {open ? (
                  <div className="border-t border-ink/10 px-2 py-3 dark:border-white/10">
                    {stores.length === 0 ? (
                      <p className="px-3 text-xs text-ink/58 dark:text-frost-muted">Nenhuma empresa provisionada nesta conta.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[620px] border-collapse text-xs">
                          <thead>
                            <tr className="border-b border-ink/10 bg-zinc-100/90 text-[10px] font-bold uppercase tracking-wide text-ink dark:border-white/10 dark:bg-zinc-900/90 dark:text-mist">
                              <th className="py-2 px-3 text-left">Loja</th>
                              <th className="py-2 px-3 text-left">Plano SaaS</th>
                              <th className="py-2 px-3 text-center">Bloqueio</th>
                              <th className="py-2 px-3 text-right">Pendências</th>
                              <th className="py-2 px-3 text-right">Próximo venc.</th>
                              <th className="py-2 px-3 text-right">Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stores.map((t) => (
                              <tr key={t.id} className="border-b border-ink/5 dark:border-white/5">
                                <td className="py-2 px-3">
                                  <div className="font-semibold text-ink dark:text-mist">{t.nome}</div>
                                  <div className="font-mono text-[10px] text-ink/58 dark:text-frost-muted">{t.slug}</div>
                                </td>
                                <td className="py-2 px-3 font-mono text-[10px] text-ink/85 dark:text-mist">
                                  {t.subscription_plan_key || 'gold'}
                                </td>
                                <td className="py-2 px-3 text-center">
                                  {Number(t.billing_overdue_blocked) === 1 ? (
                                    <span className="rounded-full bg-rose-500/20 px-2 py-0.5 font-bold uppercase text-rose-800 dark:text-rose-200">
                                      Sim
                                    </span>
                                  ) : (
                                    <span className="text-ink/60 dark:text-frost-muted">Não</span>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-right tabular-nums text-ink dark:text-mist">
                                  {t.sub_pending_cnt ?? 0}
                                </td>
                                <td className="py-2 px-3 text-right font-mono whitespace-nowrap text-ink dark:text-frost">
                                  {t.sub_next_due_date ? String(t.sub_next_due_date).slice(0, 10) : '—'}
                                </td>
                                <td className="py-2 px-3 text-right">
                                  <div className="flex flex-col items-end gap-1">
                                    {Number(t.sub_pending_cnt) > 0 ? (
                                      <AdminSubscriptionPayButton
                                        tenantId={Number(t.id)}
                                        className="text-[10px] font-bold uppercase text-sky-800 underline dark:text-sky-200"
                                        label="Pagar no portal"
                                      />
                                    ) : null}
                                    <AdminSubscriptionPortalLink
                                      tenantId={Number(t.id)}
                                      payNow={Number(t.sub_pending_cnt) > 0}
                                      className="text-[10px] font-bold uppercase text-violet-700 underline dark:text-violet-300"
                                    >
                                      {Number(t.sub_pending_cnt) > 0 ? 'Abrir checkout' : 'Ver mensalidade'}
                                    </AdminSubscriptionPortalLink>
                                    <button
                                      type="button"
                                      className="text-[10px] font-bold uppercase text-ink/55 underline dark:text-frost-muted"
                                      onClick={() => {
                                        setOwnerFilterCharges('');
                                        setTenantFilterCharges(String(t.id));
                                        setChargeBucket('');
                                        setChargeStatus('pending');
                                        setChargeOffset(0);
                                      }}
                                    >
                                      Ver cobranças
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="space-y-4 rounded-3xl border border-ink/10 bg-white/70 p-6 dark:border-white/10 dark:bg-white/[0.06]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h4 className="font-display text-lg font-semibold text-ink dark:text-mist">Todas as cobranças</h4>
            <p className="mt-1 text-xs text-ink/60 dark:text-frost-muted">
              Filtre por loja, responsável, Software House ou período. Edição apenas em cobranças{' '}
              <span className="font-semibold">em aberto</span>.
            </p>
          </div>
          <button
            type="button"
            disabled={pdfBusy || busyList}
            onClick={() => {
              generateFinancePdf().catch(() => {});
            }}
            className="rounded-2xl border border-cyan-600/40 bg-cyan-500/15 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-cyan-950 shadow-sm disabled:opacity-50 dark:border-cyan-400/40 dark:bg-cyan-500/20 dark:text-cyan-50"
          >
            {pdfBusy ? 'A gerar…' : 'Gerar PDF'}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            className="rounded-xl border border-ink/15 bg-white px-3 py-2 text-sm text-ink dark:border-white/15 dark:bg-[#0f1815] dark:text-frost"
            value={chargeBucket}
            onChange={(e) => {
              setChargeBucket(e.target.value);
              setChargeOffset(0);
            }}
          >
            <option value="">Período</option>
            <option value="overdue">Só em atraso</option>
            <option value="due_week">Vence em 7 dias</option>
            <option value="future_pending">Pendentes mais distantes</option>
          </select>
          <select
            className="rounded-xl border border-ink/15 bg-white px-3 py-2 text-sm text-ink dark:border-white/15 dark:bg-[#0f1815] dark:text-frost"
            value={chargeStatus}
            onChange={(e) => {
              setChargeStatus(e.target.value);
              setChargeOffset(0);
            }}
          >
            <option value="">Todos os status</option>
            <option value="pending">Em aberto</option>
            <option value="paid">Pago</option>
            <option value="cancelled">Cancelado</option>
          </select>
          <select
            className="min-w-[10rem] rounded-xl border border-ink/15 bg-white px-3 py-2 text-sm text-ink dark:border-white/15 dark:bg-[#0f1815] dark:text-frost"
            value={tenantFilterCharges}
            onChange={(e) => {
              setTenantFilterCharges(e.target.value);
              setChargeOffset(0);
            }}
          >
            <option value="">Todas empresas</option>
            {tenantRows.map((t) => (
              <option key={t.id} value={String(t.id)}>
                #{t.id} {t.nome}
              </option>
            ))}
          </select>
          <select
            className="min-w-[10rem] rounded-xl border border-ink/15 bg-white px-3 py-2 text-sm text-ink dark:border-white/15 dark:bg-[#0f1815] dark:text-frost"
            value={ownerFilterCharges}
            onChange={(e) => {
              setOwnerFilterCharges(e.target.value);
              setChargeOffset(0);
            }}
          >
            <option value="">Todos donos</option>
            {clients.map((u) => (
              <option key={u.id} value={String(u.id)}>
                {u.name} (#{u.id})
              </option>
            ))}
          </select>
          <select
            className="min-w-[10rem] rounded-xl border border-ink/15 bg-white px-3 py-2 text-sm text-ink dark:border-white/15 dark:bg-[#0f1815] dark:text-frost"
            value={shFilterCharges}
            onChange={(e) => {
              setShFilterCharges(e.target.value);
              setChargeOffset(0);
            }}
          >
            <option value="">Todas as SH</option>
            <option value="none">Sem Software House</option>
            {softwareHouses.map((sh) => (
              <option key={sh.id} value={String(sh.id)}>
                {sh.nome}
              </option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] border-collapse text-xs">
            <thead>
              <tr className="border-b border-ink/10 bg-zinc-100/90 text-[10px] font-bold uppercase tracking-wide text-ink dark:border-white/10 dark:bg-zinc-900/90 dark:text-mist">
                <th className="py-2 px-2">Código</th>
                <th className="py-2 px-2">Loja / responsável</th>
                <th className="py-2 px-2">SH</th>
                <th className="py-2 px-2 text-right">Valor</th>
                <th className="py-2 px-2">Status</th>
                <th className="py-2 px-2 whitespace-nowrap">Vencimento</th>
                <th className="py-2 px-2 whitespace-nowrap">Período</th>
                <th className="py-2 px-2 whitespace-nowrap">Pago em</th>
                <th className="py-2 px-2">Mercado Pago</th>
                <th className="py-2 px-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {charges.map((row) => {
                const refToday = String(dash?.today || localYmd()).slice(0, 10);
                const st = rowBillingLabel(row, refToday);
                const due = String(row.due_date || '').slice(0, 10);
                const overdueRow = row.status === 'pending' && /^\d{4}-\d{2}-\d{2}$/.test(due) && due < refToday;

                const rowTone = overdueRow ? 'bg-rose-500/[0.06]' : '';
                return (
                  <tr key={`${row.id}`} className={`border-b border-ink/5 dark:border-white/5 ${rowTone}`}>
                    <td className="py-2 px-2 font-mono tabular-nums text-ink dark:text-mist">{row.id}</td>
                    <td className="py-2 px-2">
                      <div className="max-w-[12rem] truncate font-medium text-ink dark:text-mist" title={row.tenant_nome}>
                        {row.tenant_nome}
                      </div>
                      <div className="text-[10px] text-ink/58 dark:text-frost-muted">
                        @{row.slug} · {row.owner_name} (ID {row.owner_user_id})
                      </div>
                    </td>
                    <td className="py-2 px-2 text-[11px] text-ink/75 dark:text-frost-muted">
                      {row.software_house_nome ? String(row.software_house_nome) : '—'}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums font-semibold text-ink dark:text-mist">{formatBrl(row.amount_brl)}</td>
                    <td className="py-2 px-2 text-ink dark:text-mist">{st}</td>
                    <td className="py-2 px-2 whitespace-nowrap font-mono text-ink dark:text-frost">
                      {String(row.due_date || '').slice(0, 10)}
                    </td>
                    <td className="py-2 px-2 font-mono text-[10px] text-ink/80 dark:text-frost-muted">{row.period_label || '—'}</td>
                    <td className="py-2 px-2 whitespace-nowrap text-ink/70 dark:text-frost-muted">
                      {row.paid_at ? String(row.paid_at).slice(0, 19) : '—'}
                    </td>
                    <td className="py-2 px-2 font-mono text-[10px] text-ink/70 dark:text-frost-muted">
                      {row.mp_payment_id ? `${String(row.mp_payment_id).slice(0, 10)}…` : row.mp_preference_id ? 'pref' : '—'}
                    </td>
                    <td className="py-2 px-2 whitespace-nowrap">
                      {String(row.status) === 'pending' ? (
                        <div className="flex flex-col items-end gap-1">
                          <AdminSubscriptionPayButton
                            tenantId={Number(row.tenant_id)}
                            className="text-[10px] font-bold uppercase text-sky-800 underline dark:text-sky-200"
                            label="Pagar"
                          />
                          <button
                            type="button"
                            onClick={() => openEdit(row)}
                            className="text-[10px] font-bold uppercase text-violet-700 underline dark:text-violet-300"
                          >
                            editar
                          </button>
                        </div>
                      ) : (
                        <span className="text-[10px] text-ink/45 dark:text-frost-muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {busyList ? (
          <p className="text-xs font-semibold uppercase text-ink/55 dark:text-frost-muted">A actualizar lista…</p>
        ) : charges.length === 0 ? (
          <p className="text-sm text-ink/58 dark:text-frost-muted">Nenhuma linha nos filtros actuais.</p>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-3 text-[11px]">
          <span className="tabular-nums text-ink/60 dark:text-frost-muted">
            Total {chargesTotal}{' · '}
            {chargesTotal === 0
              ? '0 na página'
              : `${chargeOffset + 1}–${Math.min(chargeOffset + chargeLimit, chargesTotal)}`}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={chargeOffset <= 0 || busyList}
              onClick={() => setChargeOffset((x) => Math.max(0, x - chargeLimit))}
              className="rounded-lg border border-ink/15 px-3 py-1 text-xs font-bold uppercase text-ink dark:text-mist disabled:opacity-40 dark:border-white/15"
            >
              Anterior
            </button>
            <button
              type="button"
              disabled={chargeOffset + chargeLimit >= chargesTotal || busyList}
              onClick={() => setChargeOffset((x) => x + chargeLimit)}
              className="rounded-lg border border-ink/15 px-3 py-1 text-xs font-bold uppercase text-ink dark:text-mist disabled:opacity-40 dark:border-white/15"
            >
              Seguinte
            </button>
          </div>
        </div>
      </div>

      {editCharge ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm">
          <form
            onSubmit={saveChargeEdit}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-ink/10 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-[#101820]"
          >
            <h5 className="font-display text-lg font-semibold text-ink dark:text-mist">Editar cobrança SaaS</h5>
            <p className="mt-1 text-[11px] text-ink/55 dark:text-frost-muted">
              Cobrança #{editCharge.id} · {editCharge.tenant_nome}. Alterar valor ou datas invalida uma preferência MP antiga —
              novo checkout gera novo link. Pagamento do cliente (ou teste admin) em{' '}
              <AdminSubscriptionPortalLink
                tenantId={Number(editCharge.tenant_id)}
                payNow
                className="font-semibold text-sky-700 underline dark:text-sky-300"
              >
                /app/mensalidade
              </AdminSubscriptionPortalLink>
              .
            </p>
            <label className="mt-5 block text-xs font-semibold text-ink dark:text-frost">
              Valor R$
              <input
                required
                className="mt-1 w-full rounded-xl border border-ink/15 bg-white px-3 py-2 font-mono text-sm text-ink dark:border-white/15 dark:bg-[#0f1815] dark:text-frost"
                value={editForm.amount_brl}
                onChange={(e) => setEditForm({ ...editForm, amount_brl: e.target.value })}
                placeholder="197,90"
              />
            </label>
            <label className="mt-3 block text-xs font-semibold text-ink dark:text-frost">
              Vencimento
              <input
                required
                type="date"
                className="mt-1 w-full rounded-xl border border-ink/15 bg-white px-3 py-2 text-sm text-ink dark:border-white/15 dark:bg-[#0f1815] dark:text-frost"
                value={editForm.due_date}
                onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
              />
            </label>
            <label className="mt-3 block text-xs font-semibold text-ink dark:text-frost">
              Rótulo período (ex. 2026-05 — opcional)
              <input
                className="mt-1 w-full rounded-xl border border-ink/15 bg-white px-3 py-2 text-sm text-ink dark:border-white/15 dark:bg-[#0f1815] dark:text-frost"
                value={editForm.period_label}
                onChange={(e) => setEditForm({ ...editForm, period_label: e.target.value })}
              />
            </label>
            <label className="mt-3 block text-xs font-semibold text-ink dark:text-frost">
              Início período (YYYY-MM-DD, vazio para limpar)
              <input
                type="date"
                className="mt-1 w-full rounded-xl border border-ink/15 bg-white px-3 py-2 font-mono text-sm text-ink dark:border-white/15 dark:bg-[#0f1815] dark:text-frost"
                value={editForm.period_start}
                onChange={(e) => setEditForm({ ...editForm, period_start: e.target.value })}
              />
            </label>
            <label className="mt-3 block text-xs font-semibold text-ink dark:text-frost">
              Fim período (opcional / vazio)
              <input
                type="date"
                className="mt-1 w-full rounded-xl border border-ink/15 bg-white px-3 py-2 font-mono text-sm text-ink dark:border-white/15 dark:bg-[#0f1815] dark:text-frost"
                value={editForm.period_end}
                onChange={(e) => setEditForm({ ...editForm, period_end: e.target.value })}
              />
            </label>

            <div className="mt-6 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setEditCharge(null)}
                className="rounded-xl border border-ink/15 px-4 py-2 text-xs font-bold uppercase text-ink dark:border-white/15 dark:text-frost"
              >
                Fechar
              </button>
              <button
                type="submit"
                disabled={editSaving}
                className="rounded-xl bg-violet-600 px-4 py-2 text-xs font-bold uppercase text-white dark:bg-violet-500"
              >
                Gravar alterações
              </button>
              <button
                type="button"
                disabled={editSaving}
                onClick={(e) => {
                  e.preventDefault();
                  cancelChargeConfirmed().catch(() => {});
                }}
                className="ml-auto rounded-xl border border-rose-500/50 px-4 py-2 text-xs font-bold uppercase text-rose-700 dark:text-rose-200"
              >
                Cancelar cobrança anular
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

function MiniChargeTable({ title, rows, onEdit, showPay = false }) {
  return (
    <div className="space-y-2 rounded-3xl border border-ink/10 bg-white/70 p-5 dark:border-white/10 dark:bg-white/[0.06]">
      <h5 className="font-display text-base font-semibold text-ink dark:text-mist">{title}</h5>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] border-collapse text-[11px]">
          <thead>
            <tr className="border-b border-ink/10 bg-zinc-100/90 text-[10px] font-bold uppercase tracking-wide text-ink dark:border-white/10 dark:bg-zinc-900/90 dark:text-mist">
              <th className="py-2 pr-2 text-left">Loja</th>
              <th className="py-2 pr-2 text-left">Cliente</th>
              <th className="py-2 pr-2 text-right">Valor</th>
              <th className="py-2 text-left">Venc.</th>
              <th className="py-2 w-28 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={String(r.id)} className="border-b border-ink/5 dark:border-white/5">
                <td className="py-2 pr-2 font-medium text-ink dark:text-mist">{r.tenant_nome}</td>
                <td className="py-2 pr-2 text-ink dark:text-frost">{r.owner_name}</td>
                <td className="py-2 pr-2 text-right tabular-nums font-semibold text-ink dark:text-mist">{formatBrl(r.amount_brl)}</td>
                <td className="py-2 font-mono whitespace-nowrap text-ink dark:text-frost">
                  {String(r.due_date || '').slice(0, 10)}
                </td>
                <td className="py-2 whitespace-nowrap">
                  <div className="flex flex-col items-end gap-1">
                    {showPay && String(r.status) === 'pending' ? (
                      <AdminSubscriptionPayButton
                        tenantId={Number(r.tenant_id)}
                        className="text-[10px] font-bold uppercase text-sky-800 underline dark:text-sky-200"
                        label="Pagar"
                      />
                    ) : null}
                    <button
                      type="button"
                      className="text-[10px] font-bold uppercase text-violet-700 underline dark:text-violet-300"
                      onClick={() => onEdit(r)}
                    >
                      editar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(!rows || rows.length === 0) ? <p className="text-[11px] text-ink/58 dark:text-frost-muted">Lista vazia.</p> : null}
    </div>
  );
}
